import React, { Component, PropTypes} from 'react'
import CSSModules from 'react-css-modules'
import Styles from './Chart.css'
import {Row, Col} from 'react-flexbox-grid'
import {Doughnut} from 'react-chartjs'
import format from '../../helpers/format'

const chartOptions = {
  segmentStrokeWidth: 0,
  percentageInnerCutout: 70,
  animationEasing: 'easeOutQuad',
  // String - Template string for single tooltips
  tooltipTemplate: "<%if (label){%><%=label %> <%}%><%= value + ' %' %>",
  // String - Template string for multiple tooltips
  multiTooltipTemplate: "<%= value + ' %' %>",
}

@CSSModules(Styles)
export default class Chart extends Component {
  static propTypes = {
    renderBox: PropTypes.func.isRequired,
    currency: PropTypes.string.isRequired,
    invested: PropTypes.number.isRequired,
    consumerPerc: PropTypes.number,
    bankPerc: PropTypes.number,
    producerPerc: PropTypes.number,
    dffPerc: PropTypes.number,
    consumerHoldings: PropTypes.array,
    financierHoldings: PropTypes.array,
    producerHoldings: PropTypes.array,
    fHoldingsMap: PropTypes.object,
    famType: PropTypes.bool,
  }

  constructor(props){
    super(props)
    this.state = {
      active: 'default',
    }
  }

  clickViewCategory = (catName, val) =>{
    if (this.state.active === catName || val === 0) {
      this.setState({active: 'default'})
    } else {
      this.setState({active: catName})
    }
  }

  formatWeight = (el) => {
    const weight = this.props.fHoldingsMap[el.id].scaled_weighting
    const formattedWeight = Math.round(weight * 100 * 100) / 100
    return formattedWeight
  }

  getChartData = (
      famType,
      dffPerc,
      producerPerc,
      bankPerc,
      consumerPerc, 
      producerHoldings,
      financierHoldings,
      consumerHoldings,
  ) => {
    let chartData = []

    const clrs = {
      redorange : '#DE450A',
      orange : '#F2961C',
      yellow : '#DFB553',
      gray : '#D5D5D5',
      black: '#333333'
    }

    const cleanItem = {
      value: 100,
      label: 'Other',
      color: clrs.gray // gray
    }

    const dfDiff = 100.0 - (Math.round(dffPerc * 100) / 100)
    const pDiff = 100.0 - (Math.round(producerPerc * 100) / 100)
    const fDiff = 100.0 - (Math.round(bankPerc * 100) / 100)
    const cDiff = 100.0 - (Math.round(consumerPerc * 100) / 100)

    if (this.state.active === 'default') {
      if (dffPerc === 0) {
        chartData.push(cleanItem)
      } else {
        chartData.push({
          value: dffPerc,
          label: 'Deforestation-risk holdings',
          color: clrs.black
        })
        chartData.push({
          value:  dfDiff,
          label: 'Other',
          color: clrs.gray 
        })
      }
    } else if (this.state.active === 'producers') {
      if (producerPerc === 0) {
        chartData.push(cleanItem)
      } else {
        if (famType) {
          chartData.push({
            value: producerPerc,
            label: 'Commodity producers',
            color: clrs.redorange
          })
          chartData.push({
            value:  pDiff,
            label: 'Other',
            color: clrs.gray 
          })
        } else {
          chartData = producerHoldings.map((el, ix) => {
            return {
              value: this.formatWeight(el),
              label: el.security_name,
              color: clrs.redorange
            }
          })          
        }
      }
    } else if (this.state.active === 'banks') {
      if (bankPerc === 0) {
        chartData.push(cleanItem)
      } else {
        if (famType) {
          chartData.push({
            value: bankPerc,
            label: 'Banks and lenders',
            color: clrs.orange
          })
          chartData.push({
            value:  fDiff,
            label: 'Other',
            color: clrs.gray 
          })
        } else {
          chartData = financierHoldings.map((el, ix) => {
            return {
              value: this.formatWeight(el),
              label: el.security_name,
              color: clrs.orange
            }
          })
        }
      }
    } else if (this.state.active === 'consumers') {
      if (consumerPerc === 0) {
        chartData.push(cleanItem)
      } else {
        if (famType) {
          chartData.push({
            value: consumerPerc,
            label: 'Major consumer brands',
            color: clrs.yellow
          })
          chartData.push({
            value:  cDiff,
            label: 'Other',
            color: clrs.gray 
          })
        } else {
          chartData = consumerHoldings.map((el, ix) => {
            return {
              value: this.formatWeight(el),
              label: el.security_name,
              color: clrs.yellow
            }
          })
        }
      }
    }
    return chartData
  }

  render() {
    const { famType, dffPerc, producerPerc, bankPerc, consumerPerc, producerHoldings, financierHoldings, consumerHoldings } = this.props

    const chartData = this.getChartData(
      famType,
      dffPerc,
      producerPerc,
      bankPerc,
      consumerPerc, 
      producerHoldings,
      financierHoldings,
      consumerHoldings,
    )

    const producersIcon = require('../../../static/images/icn-producers.svg')
    const banksIcon = require('../../../static/images/icn-banks.svg')
    const consumersIcon = require('../../../static/images/icn-consumers.svg')

    const producersInactiveIcon = require('../../../static/images/icn-producers-g.svg')
    const banksInactiveIcon = require('../../../static/images/icn-banks-g.svg')
    const consumersInactiveIcon = require('../../../static/images/icn-consumers-g.svg')

    const pIcon = producerPerc === 0 ? producersInactiveIcon : producersIcon
    const fIcon = bankPerc === 0 ? banksInactiveIcon : banksIcon
    const cIcon = consumerPerc === 0 ? consumersInactiveIcon : consumersIcon

    const producersStyle = producerPerc === 0 ?
      'blank-label' :
      (
        this.state.active === 'producers' ?
          'producers-label-actv' :
          'producers-label'
      )

    const banksStyle = bankPerc === 0 ?
      'blank-label' :
      (
        this.state.active === 'banks' ?
          'banks-label-actv' :
          'banks-label'
      )

    const consumerStyle = consumerPerc === 0 ?
      'blank-label' :
      (
        this.state.active === 'consumers' ?
          'consumers-label-actv' :
          'consumers-label'
      )

    return <div styleName='grade-chart-wrapper'>
      <Row styleName='grade-chart'>
        <Col xs={12} sm={6} md={4} styleName='box'>
          { this.props.renderBox() }
        </Col>
        <Col xs={12} sm={6} md={4} styleName='chart'>
          { /* simply changing the chartData and chartOptions in the same
               <Doughnut> causes issues with the colors. The wrong colors get
               assigned to the wrong bars. To fix this we render a different
               <Doughnut> for each set of chartData + chartOptions. */ }
          {
            this.state.active === 'default' ?
              <Doughnut
                data={chartData}
                options={chartOptions}
                width="300"
                height="300"
              /> : null
          }
          {
            this.state.active === 'producers' ?
              <Doughnut
                data={chartData}
                options={chartOptions}
                width="300"
                height="300"
              /> : null
          }
          {
            this.state.active === 'banks' ?
              <Doughnut
                data={chartData}
                options={chartOptions}
                width="300"
                height="300"
              /> : null
          }
          {
            this.state.active === 'consumers' ?
              <Doughnut
                data={chartData}
                options={chartOptions}
                width="300"
                height="300"
              /> : null
          }
          <div styleName='chart-details'>
            <div styleName='chart-assets'>
              {
                this.state.active === 'producers' ? producerPerc + '%' : null
              }
              {
                this.state.active === 'banks' ? bankPerc + '%' : null
              }
              {
                this.state.active === 'consumers' ? consumerPerc + '%' : null
              }
              {
                this.state.active === 'default' ? dffPerc + '%' : null
              }
            </div>
            <div>
              INVESTED IN
              {
                this.state.active === 'producers'
                  ? ' AGRIBUSINESS PRODUCERS AND TRADERS'
                  : null
              }
              {
                this.state.active === 'banks'
                  ? ' BANKS AND LENDERS'
                  : null
              }
              {
                this.state.active === 'consumers'
                  ? ' MAJOR CONSUMER BRANDS'
                  : null
              }
              {
                this.state.active === 'default'
                  ? ' DEFORESTATION-RISK COMPANIES'
                  : null
              }
            </div>
          </div>
        </Col>
        <Col xs={12} sm={12} md={4} styleName='labels'>
          <div
            styleName='producers'
            onClick={
              ()=>{ this.clickViewCategory('producers', producerPerc) }
            }
          >
            <img styleName='po-icon' src={pIcon} alt='producers' />
            <div styleName={producersStyle}>
              <div styleName='title'>
                AGRIBUSINESS
              </div>
              <div styleName='intro'>
                deforestation-risk producers and traders of palm oil,
                paper/pulp, rubber, timber, cattle, and soy
              </div>
            </div>
          </div>
          <div
            styleName='banks'
            onClick={
              ()=>{ this.clickViewCategory('banks', bankPerc) }
            }
          >
            <img styleName='po-icon' src={fIcon} alt='banks' />
            <div styleName={banksStyle}>
              <div styleName='title'>
                BANKS AND LENDERS
              </div>
              <div styleName='intro'>
                that finance deforestation-risk agribusiness producers via
                lending and underwriting
              </div>
            </div>
          </div>
          <div
            styleName='consumers'
            onClick={
              ()=>{ this.clickViewCategory('consumers', consumerPerc) }
            }
          >
            <img styleName='po-icon' src={cIcon} alt='consumers' />
            <div styleName={consumerStyle}>
              <div styleName='title'>
                MAJOR CONSUMER BRANDS
              </div>
              <div styleName='intro'>
                that use deforestation-risk agribusiness commodities in
                products
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  }
}

